import css from "./NumberInput.sass?inline"
import {Events, Html} from "@opendaw/lib-dom"
import {
    int,
    isDefined,
    isInstanceOf,
    Lifecycle,
    MutableObservableValue,
    StringMapping,
    ValueGuard
} from "@opendaw/lib-std"
import {ClipboardPayload} from "./ClipboardPayload"
import {createElement} from "@opendaw/lib-jsx"

const defaultClassName = Html.adoptStyleSheet(css, "NumberInput")

type Construct = {
    lifecycle: Lifecycle
    model: MutableObservableValue<number>
    mapper?: StringMapping<number>
    negativeWarning?: boolean
    className?: string
    maxChars?: int
    step?: number
    guard?: ValueGuard<number>
}

export const NumberInput = ({
                                lifecycle, model, negativeWarning, className, maxChars, mapper, step, guard
                            }: Construct) => {
    step ??= 1.0
    maxChars ??= 3
    mapper ??= StringMapping.numeric({})
    const input: HTMLElement = (
        <div contentEditable="true"
             style={{width: `calc(0.5em + ${maxChars * 6 + 1}px)`}}/>)
    const element: HTMLElement = (
        <div className={Html.buildClassList(defaultClassName, className)}>
            {input}
        </div>
    )
    const updateDigits = () => {
        const value = model.getValue()
        element.classList.toggle("negative", negativeWarning === true && value < 0)
        input.textContent = mapper.x(value).value
    }
    lifecycle.ownAll(
        model.subscribe(updateDigits),
        Events.subscribe(element, "focusin", (event: Event) => {
            if (!isInstanceOf(event.target, HTMLElement)) {return}
            Html.selectContent(event.target)
        }),
        Events.subscribe(element, "focusout", (event: Event) => {
            if (!isInstanceOf(event.target, HTMLElement)) {return}
            const result = mapper.y(event.target.textContent ?? "")
            if (result.type === "explicit" && !isNaN(result.value)) {
                model.setValue(isDefined(guard) ? guard.guard(result.value) : result.value)
            }
            updateDigits()
            Html.unselectContent(event.target)
        }),
        Events.subscribe(element, "copy", (event: ClipboardEvent) => {
            event.preventDefault()
            event.clipboardData?.setData("application/json", ClipboardPayload.write("number", model.getValue()))
        }),
        Events.subscribe(element, "paste", (event: ClipboardEvent) => {
            ClipboardPayload.read(event.clipboardData?.getData("application/json"), "number").ifSome(value => {
                event.preventDefault()
                model.setValue(isDefined(guard) ? guard.guard(Number(value)) : Number(value))
            })
        }),
        Events.subscribe(element, "keydown", (event: KeyboardEvent) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {return}
            const target = event.target
            if (!isInstanceOf(target, HTMLElement)) {return}
            switch (event.code) {
                case "ArrowUp": {
                    event.preventDefault()
                    const result = mapper.y(target.textContent ?? "")
                    if (result.type !== "explicit" || isNaN(result.value)) {return}
                    const nextValue: int = result.value + step
                    model.setValue(isDefined(guard) ? guard.guard(nextValue) : nextValue)
                    Html.selectContent(target)
                    break
                }
                case "ArrowDown": {
                    event.preventDefault()
                    const result = mapper.y(target.textContent ?? "")
                    if (result.type !== "explicit" || isNaN(result.value)) {return}
                    const nextValue: int = result.value - step
                    model.setValue(isDefined(guard) ? guard.guard(nextValue) : nextValue)
                    Html.selectContent(target)
                    break
                }
                case "Enter": {
                    event.preventDefault()
                    const result = mapper.y(target.textContent ?? "")
                    if (result.type === "explicit" && !isNaN(result.value)) {
                        model.setValue(isDefined(guard) ? guard.guard(result.value) : result.value)
                    }
                    target.blur()
                    break
                }
                case "Digit0": case "Digit1": case "Digit2": case "Digit3": case "Digit4":
                case "Digit5": case "Digit6": case "Digit7": case "Digit8": case "Digit9":
                case "Numpad0": case "Numpad1": case "Numpad2": case "Numpad3": case "Numpad4":
                case "Numpad5": case "Numpad6": case "Numpad7": case "Numpad8": case "Numpad9":
                case "Tab":
                case "ArrowLeft":
                case "ArrowRight":
                case "Minus":
                case "NumpadSubtract":
                case "Backspace": {
                    break // Allow
                }
                default: {
                    console.debug("ignore", event.code)
                    event.preventDefault()
                }
            }
        })
    )
    updateDigits()
    return element
}